import { useCallback, useEffect, useMemo, useState } from 'react';
import BeachList from './components/BeachList';
import BottomSheet, { type Detent } from './components/BottomSheet';
import LoadingOverlay from './components/LoadingOverlay';
import TimeControls, { fmtDay } from './components/TimeControls';
import WaveMap from './components/WaveMap';
import { BEACHES } from './data/beaches';
import { useMediaQuery } from './hooks/useMediaQuery';
import { bestWindow, hourlyBest, rankedAt } from './lib/forecast';
import { currentHourIndex, fetchForecast, type ForecastData } from './lib/openMeteo';

export default function App() {
  const [data, setData] = useState<ForecastData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadedAt, setLoadedAt] = useState<string | null>(null);
  const [selIdx, setSelIdx] = useState(0);
  const [nowIdx, setNowIdx] = useState(0);
  const [bestNote, setBestNote] = useState('');
  const [focus, setFocus] = useState<{ id: string; nonce: number } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reveal, setReveal] = useState<string | null>(null);
  const [detent, setDetent] = useState<Detent>('mid');
  const [sheetH, setSheetH] = useState(0);

  /** the bottom sheet only makes sense on a tall, narrow screen — see index.css */
  const compact = useMediaQuery('(max-width: 760px) and (min-height: 501px)');

  useEffect(() => {
    const ctrl = new AbortController();
    fetchForecast(ctrl.signal)
      .then((d) => {
        const now = currentHourIndex(d.times);
        setData(d);
        setSelIdx(now);
        setNowIdx(now);
        setLoadedAt(new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }));
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : String(err));
        console.error(err);
      });
    return () => ctrl.abort();
  }, []);

  const ranked = useMemo(() => (data ? rankedAt(data, selIdx) : []), [data, selIdx]);
  const best = useMemo(() => (data ? hourlyBest(data) : []), [data]);

  const selectTime = (t: number) => {
    if (!data) return;
    setSelIdx(Math.max(0, Math.min(data.times.length - 1, t)));
  };

  const focusBeach = useCallback((id: string) => {
    setSelectedId(id);
    setFocus((f) => ({ id, nonce: (f?.nonce ?? 0) + 1 }));
  }, []);

  /** tapping a card hands the screen back to the map */
  const selectFromList = (id: string) => {
    focusBeach(id);
    if (compact) setDetent('peek');
  };

  /** tapping a marker highlights the matching card without covering the map */
  const selectFromMap = useCallback(
    (id: string) => {
      setSelectedId(id);
      setReveal(id);
    },
    [],
  );

  // clear so the same beach can be revealed again later
  useEffect(() => {
    if (!reveal) return;
    const id = setTimeout(() => setReveal(null), 400);
    return () => clearTimeout(id);
  }, [reveal]);

  const findBest = () => {
    if (!data) return;
    const w = bestWindow(data);
    if (!w) return;
    const b = BEACHES[w.i];
    setSelIdx(w.t);
    focusBeach(b.id);
    setReveal(b.id);
    if (compact) setDetent('mid');
    setBestNote(
      `${b.name} · ${fmtDay(data.times[w.t].slice(0, 10))} ${data.times[w.t].slice(11, 16)} · ` +
        `score ${w.score.toFixed(1)}/10 — the best daylight window in the next 7 days.`,
    );
  };

  const status = error ? 'data error' : loadedAt ? `live · ${loadedAt}` : 'loading…';

  return (
    <div className="app">
      <header className="topbar">
        <h1>
          SWELL<span className="wave-mark">SCOUT</span>
        </h1>
        <span className="sub">Zygi → Larnaca → Cape Greco → Protaras</span>
        <span className="status">
          <span className={error ? 'dot err' : 'dot'} />
          {status}
        </span>
      </header>

      <div id="layout">
        <WaveMap
          ranked={ranked}
          focus={focus}
          selectedId={selectedId}
          bottomInset={compact ? sheetH : 0}
          compact={compact}
          onSelectBeach={selectFromMap}
        />

        <BottomSheet
          enabled={compact}
          detent={detent}
          onDetentChange={setDetent}
          onVisibleHeight={setSheetH}
          head={
            data && (
              <TimeControls
                times={data.times}
                selIdx={selIdx}
                nowIdx={nowIdx}
                best={best}
                onSelectTime={selectTime}
                onFindBest={findBest}
              />
            )
          }
        >
          {bestNote && (
            <p className="bestnote">
              <span className="flagmark">⚑</span>
              {bestNote}
            </p>
          )}
          {data && (
            <BeachList
              ranked={ranked}
              data={data}
              selIdx={selIdx}
              nowIdx={nowIdx}
              selectedId={selectedId}
              revealId={reveal}
              onSelectBeach={selectFromList}
            />
          )}
          <footer>
            <p>0 = jetski glass · 10 = stay out. Score = beach-exposure-adjusted wave height.</p>
            <p>
              Data: Open-Meteo Marine (DWD/ECMWF WAM, 5 km EU grid) + Open-Meteo Forecast (wind).
              Map: © OpenStreetMap contributors.
            </p>
          </footer>
        </BottomSheet>
      </div>

      {!data && (
        <LoadingOverlay
          message={
            error
              ? `Failed to load: ${error} — check network and reload.`
              : `fetching wave models for ${BEACHES.length} beaches…`
          }
        />
      )}
    </div>
  );
}
